import { __awaiter, __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FormBuilder, FormField, Input, InputState, InputType, Text, TextColor } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { ErrorMessage, NotifyMeError } from '@as-react/common-types';
import { CheckIcon, EnvelopeSimpleIcon, InfoIcon, XIcon } from '@yonderland/andes-icons';
import { Banner, Box, ButtonAlpha, Icon, IconButton, Label, Link } from '@yonderland/andes-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ProductsAPI from '../../api/ProductsAPI';
import { EmailValidationWrapper } from '../../components/email/EmailValidationWrapper';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { useOAuth } from '../../hooks/useOAuth';
import { analyticsActions } from '../../redux/analytics/actions/AnalyticsActions.creators';
import { productActions } from '../../redux/product/actions/ProductActions';
import { makeGetProductInfo, makeGetSelectedSku } from '../../redux/product/selectors/ProductSelector';
const emailFieldName = 'notifyMeEmail';
export const NOTIFY_ME_FORM_NAME = 'notify-me';
export const NotifyMeForm = ({ withNewDesign }) => {
    var _a;
    const { t } = useTranslation([I18N_NAMESPACES.account, I18N_NAMESPACES.general, I18N_NAMESPACES.product]);
    const { defaultRequestParams, servicesEndpoint, privacyPolicyLink } = useAemConfig();
    const formikRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [submittedEmail, setSubmittedEmail] = useState();
    const [emailFieldFocused, setEmailFieldFocused] = useState(false);
    const [isSubmitError, setIsSubmitError] = useState(false);
    const [shouldNotResetStateOnSizeChange, setShouldNotResetStateOnSizeChange] = useState(false);
    const { userInfo } = useOAuth();
    const dispatch = useDispatch();
    const initialEmail = (_a = userInfo === null || userInfo === void 0 ? void 0 : userInfo.email) !== null && _a !== void 0 ? _a : '';
    const selectedSku = useSelector(makeGetSelectedSku());
    const { productId } = useSelector(makeGetProductInfo());
    useEffect(() => {
        if (shouldNotResetStateOnSizeChange) {
            setShouldNotResetStateOnSizeChange(false);
        }
        else {
            setLoading(false);
            setSubmittedEmail(undefined);
            setEmailFieldFocused(false);
            formikRef.current.resetForm();
        }
    }, [selectedSku]);
    const onSubmit = (_a) => __awaiter(void 0, [_a], void 0, function* ({ notifyMeEmail }) {
        if (submittedEmail === notifyMeEmail || loading) {
            return;
        }
        if (!selectedSku) {
            setShouldNotResetStateOnSizeChange(true);
            dispatch(productActions.setNotifyMeError(NotifyMeError.SELECT_SIZE));
            return;
        }
        setLoading(true);
        try {
            yield ProductsAPI.notifyMeSubscribe(servicesEndpoint, defaultRequestParams, Number(productId), Number(selectedSku), notifyMeEmail);
            dispatch(analyticsActions.notifyMeRequest());
            setLoading(false);
            setEmailFieldFocused(false);
            setIsSubmitError(false);
            setSubmittedEmail(notifyMeEmail);
        }
        catch (_b) {
            setLoading(false);
            setEmailFieldFocused(false);
            setIsSubmitError(true);
        }
    });
    return (_jsx(FormBuilder, { enableReinitialize: true, name: NOTIFY_ME_FORM_NAME, innerRef: formikRef, initialValues: { notifyMeEmail: initialEmail }, validationSchema: undefined, onSubmit: onSubmit, validateOnBlur: false, children: ({ errors, touched, values, setFieldValue }) => (_jsxs(_Fragment, { children: [_jsx(Box, { paddingBlockEnd: "4", marginInline: withNewDesign ? '-4' : '0', children: isSubmitError ? (_jsx(Banner, { status: "error", text: t(ErrorMessage.API_ERROR) })) : submittedEmail ? (_jsx(Banner, { status: "success", icon: { source: InfoIcon }, text: t('product:notifyMe.success.message', { email: submittedEmail }) })) : (_jsx(Banner, { status: "informative", text: t('product:notifyMe.instruction') })) }), _jsx(Box, { paddingBlockEnd: "1", children: _jsx(Label, { htmlFor: emailFieldName, children: t('product:notifyMe.email.label') }) }), _jsx(EmailValidationWrapper, { skipValidationFor: initialEmail, emailFieldKey: emailFieldName, errorMessages: {
                        invalidEmail: t('account.subscribe.invalid'),
                        required: t('account.subscribe.invalid'),
                    }, children: (_a) => {
                        var { renderError } = _a, formFieldProps = __rest(_a, ["renderError"]);
                        return (_jsx(FormField, Object.assign({ required: true, name: emailFieldName, type: InputType.EMAIL, placeholder: t('product:notifyMe.email.placeholder'), Component: Input, maxLength: 100, dataQA: "notify_me_email_address", state: (errors.notifyMeEmail && touched.notifyMeEmail && InputState.ERROR) || undefined, onFocus: () => setEmailFieldFocused(true), adornmentEnd: !emailFieldFocused && values.notifyMeEmail === submittedEmail ? (_jsx(Icon, { source: CheckIcon, size: "sm" })) : (values.notifyMeEmail && (_jsx(IconButton, { onClick: () => setFieldValue(emailFieldName, '', true), variant: "plain", size: "sm", icon: { source: XIcon, color: 'subtle' } }))), renderError: (msg, textProps) => renderError ? renderError(msg, textProps) : _jsx(Text, Object.assign({}, textProps, { children: msg })) }, formFieldProps)));
                    } }), _jsx(Box, { paddingBlockStart: "4", children: _jsx(ButtonAlpha, { fullWidth: true, type: "submit", testId: "notify_me_submit", theme: "branded", text: t('product:notifyMe.submit.button'), state: loading ? 'loading' : undefined, iconLeft: {
                            source: EnvelopeSimpleIcon,
                        } }) }), Boolean(privacyPolicyLink) && (_jsx(Box, { paddingBlockStart: "4", children: _jsxs(Text, { color: TextColor.SUBTLE, children: [t('product:notifyMe.privacy.policy.part1'), _jsx(Link, { href: privacyPolicyLink, target: "_blank", variant: "secondary", children: t('product:notifyMe.privacy.policy.part2') }), t('product:notifyMe.privacy.policy.part3')] }) }))] })) }));
};
